/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { Box, Paper, Group, Image } from '@mantine/core';

// Styles
import classes from './conversion-factors.module.css';

// Components
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { DateFilter } from '@components/molecules/DateFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { AddConversionFactor } from '@features/resources/conversion-factors/components/AddConversionFactor';
import { Pagination } from '@components/atoms/Pagination';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Apis
import { useConversionFactorsApi } from '@features/resources/conversion-factors/api/get-conversion-factors';
import { useRemoveMultiple } from '@features/resources/conversion-factors/api/removeMultiple';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useSites } from 'shared/api/get-sites';
import { useProducers } from 'shared/api/get-producers';

// Datatable config
import { tableSchemaBase as columns } from '@features/resources/conversion-factors/conversion-factor.columns';

// Utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';

// Custom hook
import { useConversionFactor } from '@features/resources/conversion-factors/hooks/useConversionFactor';
import { DrawerConversionFactorShowMore } from '@features/resources/conversion-factors/components/DrawerConversionFactorShowMore';
import { useRemove } from '@features/resources/conversion-factors/api/remove';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { MultipleSelectionModal } from '@components/molecules/MultipleSelectionModal';
import { useEffect } from 'react';
import { TTargetColumn } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const ConversionFactor = () => {
	const { t } = useTranslation();

	const {
		containerRef,
		conversionFactorsPage,
		conversionFactorsPageSize,
		queryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		showScrollFilters,
		setOpenDrawerAddConversionFactor,
		handleapplyprojectfilter,
		ref,
		handlerowclicked,
		handlerowviewmore,
		handlerowremoveitem,
		handlechangesortstatus,
		handleselectedrecordchange,
		handlePaginationChangeSize,
		handleChangePagination,
		handleapplydatefilter,
		handleapplysitefilter,
		conversionFactorsQueryParams,
		setConversionFactorsQueryParams,
		conversionFactorsSortParams,
		conversionFactorsSelectedRecords,
		conversionFactorsSortStatus,
		handleapplyproducerfilter,
		openDrawer,
		conversionFactorsSelectedRow,
		handleCloseDrawerShowMoreConversionFactor,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		conversionFactorsOpenConfirmationModalDelete,
		handledeleteconversionfactor,
		openDrawerAddConversionFactor,
		handleCloseDrawerAddConversionFactor,
		openSelectedItemsToDeleteModal,
		handledeletemultipleselectionconfirm,
		handleclosedeletemultipleselectionmodal,
		handleremovedeletemultipleselection,
		queryParamsProducers,
		resetFilterProjects,
		setResetFilterProjects,
		setQueryParamsProjects,
		conversionFactorsDatatableOptions,
		setConversionFactorsDatatableOptions,
	} = useConversionFactor();

	// APIs
	const {
		data: projects,
		isFetched: isFetchedMeasurabilities,
	} = useMeasurabilities(queryParamsProducers);

	const { data: sites } = useSites(queryParamsProjects);

	const { data: producers } = useProducers();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useConversionFactorsApi(
		conversionFactorsPage,
		conversionFactorsPageSize,
		conversionFactorsQueryParams,
		conversionFactorsSortParams
	);

	const remove = useRemove(
		conversionFactorsPage,
		conversionFactorsPageSize
	);

	const removeMultiple = useRemoveMultiple(
		conversionFactorsPage,
		conversionFactorsPageSize
	);

	useEffect(() => {
		if (
			isFetchedMeasurabilities &&
			queryParamsProducers &&
			queryParamsProducers.entity &&
			queryParamsProducers.entity.length !== 0
		) {
			const queryParamsProjects = {
				project: projects.map(
					(_project: { id: string }) => _project.id
				),
				site: [],
			};
			setQueryParamsProjects(queryParamsProjects);
			setConversionFactorsQueryParams({
				...conversionFactorsQueryParams,
				project: projects.map(
					(_project: { id: string }) => _project.id
				),
			});
		}
	}, [isFetchedMeasurabilities, queryParamsProducers]);

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/**************************** Drawer add conversionFactor ****************************/}
			<AddConversionFactor
				opened={openDrawerAddConversionFactor}
				onClose={handleCloseDrawerAddConversionFactor}
			/>

			{/**************************** Drawer show more conversionFactor ****************************/}
			<DrawerConversionFactorShowMore
				opened={openDrawer}
				handledelete={handledeleteconversionfactor}
				onClose={
					handleCloseDrawerShowMoreConversionFactor
				}
				position={'right'}
				data={conversionFactorsSelectedRow}
			/>

			{/**************************** Delete Multiple selection modal ****************************/}

			<MultipleSelectionModal
				model="conversionFactor"
				type="delete"
				data={conversionFactorsSelectedRecords}
				onConfirm={() =>
					handledeletemultipleselectionconfirm(
						removeMultiple
					)
				}
				onClose={handleclosedeletemultipleselectionmodal}
				onRemove={handleremovedeletemultipleselection}
				opened={openSelectedItemsToDeleteModal}
			/>
			{/**************************** Delete confirmation modal ****************************/}

			<ConfirmationModal
				title={t('conversionFactors.modal.delete.title')}
				description={t(
					'conversionFactors.modal.delete.description'
				)}
				onConfirm={() =>
					handledeleteconfirmationmodalconfirm(remove)
				}
				onClose={handleclosedeleteconfirmationmodal}
				opened={
					conversionFactorsOpenConfirmationModalDelete &&
					conversionFactorsOpenConfirmationModalDelete.open
				}
			/>
			{/**************************** Content ****************************/}

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<DateFilter
											onApply={handleapplydatefilter}
											placeholder={t(
												'placeholders.searchProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
											}}
											onApply={handleapplyproducerfilter}
											label={t('global.producer')}
											size="lg"
											options={convertArrayToLabelValue(
												producers
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProducer'
											)}
											placeholder={t(
												'filters.placeholders.selectProducer'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterProjects}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProjects(_isReset);
												setResetFilterSites(_isReset);
											}}
											onApply={handleapplyprojectfilter}
											label={t('global.projects')}
											size="lg"
											options={convertArrayToLabelValue(
												projects
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProject'
											)}
											placeholder={t(
												'filters.placeholders.selectProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterSites}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterSites(_isReset);
											}}
											onApply={handleapplysitefilter}
											label={t('global.sites')}
											size="lg"
											options={convertArrayToLabelValue(
												sites
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchSite'
											)}
											placeholder={t(
												'filters.placeholders.selectSite'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.CONVERSION_FACTORS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={() => {
										setOpenDrawerAddConversionFactor(
											true
										);
									}}
								>
									{t(
										'conversionFactors.addConversionFactor'
									)}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={() => {
										setOpenDrawerAddConversionFactor(
											true
										);
									}}
								>
									{t(
										'conversionFactors.addConversionFactor'
									)}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setConversionFactorsDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={conversionFactorsDatatableOptions}
						data={data}
						onRowClick={handlerowclicked}
						onViewMore={handlerowviewmore}
						onRemoveItem={handlerowremoveitem}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={null}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={conversionFactorsSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={conversionFactorsPageSize}
					totalitems={totalItems}
					activepage={conversionFactorsPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default ConversionFactor;
